import { $getRoot } from "lexical";

/**
 * Traverses Lexical nodes and generates HTML with inline styles for color, background-color, underline, etc.
 * @param {LexicalEditor} editor - The Lexical editor instance
 * @returns {string} HTML string with inline styles
 */
export default function generateHtmlWithStylesFromNodes(editor) {
  let html = "";
  editor.getEditorState().read(() => {
    const root = $getRoot();
    html = nodeToHtml(root);
  });
  return html;
}

function styleObjToString(styleObj) {
  if (!styleObj) return "";
  if (typeof styleObj === "string") return styleObj;

  return (
    Object.entries(styleObj)
      .filter(([k, v]) => v && String(v).trim() !== "")
      .map(([k, v]) => {
        // Handle camelCase to kebab-case conversion
        const kebabKey = k.replace(/([A-Z])/g, "-$1").toLowerCase();
        return `${kebabKey}: ${v}`;
      })
      .join("; ") + (Object.keys(styleObj).length > 0 ? ";" : "")
  );
}

function nodeToHtml(node) {
  // Helper to get style object from node
  const getNodeStyleObj = (node) => {
    if (node.__style) {
      return node.__style;
    }
    return undefined;
  };

  // Helper to wrap HTML with style if styleObj exists
  const wrapWithStyle = (tag, inner, styleObj) => {
    const style = styleObjToString(styleObj);
    if (style && style.trim() !== "") {
      return `<${tag} style="${style}">${inner}</${tag}>`;
    }
    return `<${tag}>${inner}</${tag}>`;
  };

  if (node.getType() === "root") {
    return node.getChildren().map(nodeToHtml).join("");
  }
  if (node.getType() === "paragraph") {
    const styleObj = getNodeStyleObj(node);
    let inner = node.getChildren().map(nodeToHtml).join("");
    if (inner.trim() === "") {
      inner = "<br>";
    }
    return wrapWithStyle("p", inner, styleObj);
  }
  if (node.getType() === "heading") {
    const tag = `h${node.getTag()}`;
    const styleObj = getNodeStyleObj(node);
    const inner = node.getChildren().map(nodeToHtml).join("");
    return wrapWithStyle(tag, inner, styleObj);
  }
  if (node.getType() === "quote") {
    const styleObj = getNodeStyleObj(node);
    const inner = node.getChildren().map(nodeToHtml).join("");
    return wrapWithStyle("blockquote", inner, styleObj);
  }
  if (node.getType() === "linebreak") {
    return "<br>";
  }
  if (node.getType() === "list") {
    const tag = node.getListType() === "number" ? "ol" : "ul";
    const styleObj = getNodeStyleObj(node);
    const inner = node.getChildren().map(nodeToHtml).join("");
    return wrapWithStyle(tag, inner, styleObj);
  }
  if (node.getType() === "listitem") {
    const styleObj = getNodeStyleObj(node);
    const inner = node.getChildren().map(nodeToHtml).join("");
    return wrapWithStyle("li", inner, styleObj);
  }
  if (node.getType() === "link") {
    const url = node.getURL();
    const styleObj = getNodeStyleObj(node);
    const inner = node.getChildren().map(nodeToHtml).join("");
    const style = styleObjToString(styleObj);
    if (style && style.trim() !== "") {
      return `<a href="${url}" target="_blank" style="${style}">${inner}</a>`;
    }
    return `<a href="${url}" target="_blank">${inner}</a>`;
  }
  if (node.getType() === "text") {
    let text = node.getTextContent();
    const format = node.getFormat();
    let styleObj = getNodeStyleObj(node);

    // Fallback: If __style is not available, try getStyle() method
    // This handles cases where styles were set via setStyle() which may store differently
    if (!styleObj && typeof node.getStyle === "function") {
      const styleString = node.getStyle();
      if (styleString && styleString.trim() !== "") {
        // Convert style string to object for consistency
        styleObj = {};
        styleString.split(";").forEach((pair) => {
          const idx = pair.indexOf(":");
          if (idx > -1) {
            const key = pair.slice(0, idx).trim();
            const value = pair.slice(idx + 1).trim();
            if (key && value) {
              styleObj[key] = value;
            }
          }
        });
      }
    }

    // Debug: Log if we found a highlight
    if (styleObj && styleObj["background-color"]) {
      const bgColor = styleObj["background-color"];
      if (
        bgColor &&
        (bgColor.includes("yellow") ||
          bgColor.includes("ffff00") ||
          bgColor.includes("255, 255, 0"))
      ) {
      }
    }

    // Compose format tags
    if (format & 1) text = `<strong>${text}</strong>`; // bold
    if (format & 2) text = `<em>${text}</em>`; // italic
    if (format & 8) text = `<u>${text}</u>`; // underline
    if (format & 16) text = `<s>${text}</s>`; // strikethrough

    // Apply inline styles if present
    if (styleObj && Object.keys(styleObj).length > 0) {
      const style = styleObjToString(styleObj);
      if (style.trim() !== "") {
        text = `<span style="${style}">${text}</span>`;
      }
    } else if (typeof node.getStyle === "function") {
      // If styleObj is empty but getStyle() returns a string, use it directly
      const styleString = node.getStyle();
      if (styleString && styleString.trim() !== "") {
        text = `<span style="${styleString}">${text}</span>`;
      }
    }
    return text;
  }
  // fallback: just text content
  return node.getTextContent();
}
